import { reportDeviceTrace } from "../sentry";
import { analyticsController } from "../AppController";

/**
 * Diagnostics for the intermittent "device shows as unpaired" reports
 * (tracing kaitlyn+3@circadia.health). Emits to BOTH Sentry and Mixpanel so the
 * timeline survives regardless of environment, and is correlatable by the
 * logged-in user (Sentry/Mixpanel already carry the user identity).
 *
 *  - assigned_device_transition: the assigned device flipped null↔set (this is
 *    the exact moment the member home banner shows/hides "No device connected").
 *  - device_fetch_empty: a *successful* fetchAssignedDevice returned no device
 *    (or a status:0 row) — i.e. the API itself reported "no device".
 *  - device_assign_called / device_unassign_called: a frontend-initiated write,
 *    used to prove/exonerate the app as the source of a DB change.
 */
export type DeviceTraceEvent =
  | "assigned_device_transition"
  | "device_fetch_empty"
  | "device_assign_called"
  | "device_unassign_called"
  // App lifecycle / connectivity snapshot (app open, resume from background,
  // online/offline) — captures the device state *at that moment*, independent of
  // any API call, so a "showed no device right after opening / while offline"
  // report can be correlated with what the app actually knew.
  | "device_lifecycle"
  // The user-visible "No device connected yet" banner actually rendered on the
  // member home, with the surrounding network/loading context.
  | "device_no_device_banner";

/**
 * High-frequency context events that are only useful as the breadcrumb trail
 * leading up to a meaningful event — they stay out of Sentry's grouped issues
 * (no per-event issue spam) but are still added as breadcrumbs there and shipped
 * to Mixpanel as full events.
 */
const SENTRY_BREADCRUMB_ONLY: ReadonlySet<DeviceTraceEvent> = new Set([
  "device_lifecycle",
]);

/** Emits one diagnostic to both sinks. Never throws — logging must not break flows. */
export const traceDeviceEvent = (
  event: DeviceTraceEvent,
  data: Record<string, unknown> = {}
): void => {
  const payload = { ...data, at: new Date().toISOString() };
  // Escalate the actual user-visible symptom to `warning` so it stands out in
  // Sentry Logs/Issues: the device flipping to unpaired, or the empty banner
  // actually rendering.
  const isSymptom =
    event === "device_no_device_banner" ||
    (event === "assigned_device_transition" && data.becameUnpaired === true);
  try {
    reportDeviceTrace(event, payload, {
      capture: !SENTRY_BREADCRUMB_ONLY.has(event),
      level: isSymptom ? "warning" : "info",
    });
  } catch {
    /* never let Sentry logging throw */
  }
  try {
    analyticsController.logEvent(`device_${event}`, payload);
  } catch {
    /* never let Mixpanel logging throw */
  }
};

/**
 * Convenience wrapper for read sites: traces only when a *successful* fetch
 * reported "no device" (null data or a status:0 row) — the smoking-gun outcome
 * that clears the assigned device. Soft failures (success:false) are ignored
 * here since they never clear state.
 */
export const traceAssignedDeviceFetch = (params: {
  success: boolean;
  data:
    | { device_id?: string; status?: number; device_conn_status?: number }
    | null
    | undefined;
  source: string;
  clinicId?: number | null;
  patientId?: number | null;
}): void => {
  const { success, data, source, clinicId, patientId } = params;
  if (!success) return;
  // A genuinely-assigned device (truthy + not status:0) is the happy path.
  if (data && data.status !== 0) return;
  traceDeviceEvent("device_fetch_empty", {
    source,
    clinicId: clinicId ?? null,
    patientId: patientId ?? null,
    returned: !data ? "null" : "status_0",
    rawDeviceId: data?.device_id ?? null,
  });
};
